import {useMemo, useRef, useState, useCallback} from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  Skeleton,
  useColorModeValue,
  useDisclosure,
  Badge,
  Flex,
  Grid,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  useMediaQuery,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Portal,
} from '@chakra-ui/react';
import {
  TablerMessage,
  TablerBolt,
  TablerChevronDown,
  TablerFileText,
  TablerInfoCircle,
  TablerTrash,
  TablerRotate,
  TablerClock,
  TablerCircleCheck,
  TablerUsers,
  TablerDotsVertical,
  TablerEdit,
  TablerPlayerPlay,
  TablerStar,
  TablerX,
} from '../../icons';
import SessionRatingModal from './SessionRatingModal';
import TemplateSelectionView from './TemplateSelectionView';
import DetailBackButton from './DetailBackButton';
import {SCRIBE_TEMPLATES} from '../../services/scribeService';
import {resolveEncounterModality} from '../../services/encounterService';
import {
  displayLanguageName,
  isNonEnglish,
  languageFlag,
} from '../../constants/designTokens';
import {CREATE_SESSION_CARD_PADDING} from '../planner/CreateSessionFormSections';
import {PLANNER_SIDEBAR_NAME_FONT_SIZE} from '../planner/PlannerSidebarList';
import usePlannerStyles from '../planner/usePlannerStyles';
import {
  RECAP_CARD_BOUNDARY_PROPS,
  RECAP_GRAY_SECONDARY_BUTTON_PROPS,
  RECAP_OPALITE_BLUE,
  RECAP_OPALITE_BLUE_BORDER,
  RECAP_OPALITE_BLUE_TINT_08,
  RECAP_OPALITE_BLUE_TINT_12,
  RECAP_OPALITE_BLUE_TINT_14,
  RECAP_OPALITE_BLUE_TINT_15,
  RECAP_PRIMARY_BUTTON_PROPS,
} from './recapButtonStyles';

const RECAP_INFO_CLOSE_BUTTON_SIZE = '1.5rem';
const RECAP_INFO_CLOSE_BUTTON_TOP = {
  base: 'calc(1rem + 0.4375rem)',
  md: 'calc(1.25rem + 0.4375rem)',
};
const RECAP_INFO_CLOSE_BUTTON_X_ALIGN_OFFSET = '0.25rem';

// ═════════════════════════════════════════════════════════════
// NOTE GENERATION TAB
// ═════════════════════════════════════════════════════════════

// Absolute timestamp ("Today, 3:42 PM", "Yesterday, 9:10 AM",
// "Thursday, 3:42 PM", "Mar 17, 3:42 PM") used by the "previously
// generated" header. Doctors found relative phrasing ("1 day ago",
// "2 hours ago") harder to reason about than an exact day + time —
// they want to see whether a note was generated *this morning* vs.
// *yesterday evening* at a glance. We still collapse same-day and
// same-week into friendly anchors ("Today", "Yesterday", weekday)
// because those are faster to parse than a date the reader has to
// translate, but we always pair them with an exact time. Returns
// null when no date is available so callers can omit the line.
const formatExactTimestamp = (value: any) => {
  const date = toDateValue(value);
  if (!date) return null;
  const now = new Date();
  const sameDay = (a: any, b: any) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  if (sameDay(date, now)) return `Today, ${time}`;
  if (sameDay(date, yesterday)) return `Yesterday, ${time}`;

  // Within the past 6 days, lead with the weekday — "Thursday, 3:42 PM"
  // reads faster than "Mar 12, 3:42 PM" when it's still recent.
  const daysAgo = Math.floor((now.getTime() - date.getTime()) / 86_400_000);
  if (daysAgo >= 0 && daysAgo < 7) {
    const weekday = date.toLocaleDateString(undefined, {weekday: 'long'});
    return `${weekday}, ${time}`;
  }

  // Same year — drop the year to keep the line tight.
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const GENERIC_GENERATED_NOTE_LABELS = new Set([
  'clinical note',
  'generated note',
  'medical note',
  'note',
]);

const cleanNoteTypeLabel = (value: any) => {
  const label = String(value || '').trim();
  return label || '';
};

const isGenericGeneratedNoteLabel = (value: any) => {
  const label = cleanNoteTypeLabel(value).toLowerCase();
  return !label || GENERIC_GENERATED_NOTE_LABELS.has(label);
};

const formatNoteTypeValue = (value: any) => {
  const text = cleanNoteTypeLabel(value);
  if (!text) return '';
  return text
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, match => match.toUpperCase());
};

export const resolveGeneratedNoteTypeLabel = ({
  generatedNote,
  selectedTemplateId,
  allTemplates = [],
}: Record<string, any>) => {
  const directLabel = cleanNoteTypeLabel(
    generatedNote?.templateName ||
      generatedNote?.template_name ||
      generatedNote?.noteTypeName ||
      generatedNote?.note_type_name,
  );
  if (!isGenericGeneratedNoteLabel(directLabel)) return directLabel;

  const templates = [...(allTemplates || []), ...SCRIBE_TEMPLATES];
  const candidateTemplateIds = [
    generatedNote?.templateId,
    generatedNote?.template_id,
    generatedNote?.noteType,
    generatedNote?.note_type,
    selectedTemplateId,
  ]
    .map(cleanNoteTypeLabel)
    .filter(Boolean);

  for (const templateId of candidateTemplateIds) {
    const templateName = cleanNoteTypeLabel(
      templates.find((template: any) => template.id === templateId)?.name,
    );
    if (!isGenericGeneratedNoteLabel(templateName)) return templateName;
  }

  const formattedNoteType = formatNoteTypeValue(
    generatedNote?.noteType || generatedNote?.note_type,
  );
  if (!isGenericGeneratedNoteLabel(formattedNoteType)) return formattedNoteType;

  return 'Progress Note';
};

export const NoteGenerationTab = ({
  allTemplates,
  selectedTemplateId,
  setSelectedTemplateId,
  generatedNote,
  isGeneratingNote,
  onGenerateNote,
  onViewNote,
  onRegenerate,
  session,
  messages: rawMessages,
  accentColor,
  cardBg,
  borderColor,
  subtleText,
  scribeDisabled = false,
  ehrLoadFailed = false,
  onReloadTemplates = null,
  isReloadingTemplates = false,
  // Interpreter/telephonic calls on EHR-linked accounts: the Documentation
  // card offers ONE action — "Generate note" — that opens the unified
  // Encounter Review chooser where the clinical-note / telephone-encounter
  // choice lives. One button here, the fork happens on the next screen.
  onDocumentCall = null,
  // Density, decided by the parent from measured size.
  //   compact / dense → tighten vertical padding (short displays)
  compact = false,
  dense = false,
}: Record<string, any>) => {
  // When the EHR/org template load failed, never present the built-in
  // generic defaults as a valid choice — for an EHR org they're the wrong
  // templates. Show only real templates (which may be empty) alongside the
  // failure banner so the provider reloads instead of silently generating
  // against "Progress Note / Patient Intake / …".
  const builtInTemplateIds = SCRIBE_TEMPLATES.map(t => t.id);
  const displayTemplates = ehrLoadFailed
    ? (allTemplates || []).filter(
        (t: any) => !builtInTemplateIds.includes(t.id),
      )
    : allTemplates;
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const noteModalStyles = usePlannerStyles();
  const clinicalNoteAction = generatedNote ? onRegenerate : onGenerateNote;
  const generatedNoteTypeLabel = resolveGeneratedNoteTypeLabel({
    generatedNote,
    selectedTemplateId,
    allTemplates: displayTemplates,
  });
  const clinicalNoteForm = (
    <TemplateSelectionView
      titleVariant="hero"
      title="Select clinical note template"
      templates={displayTemplates}
      selectedId={selectedTemplateId}
      onSelect={setSelectedTemplateId}
      isDisabled={isGeneratingNote}
      accentColor={RECAP_OPALITE_BLUE}
      cardBg={cardBg}
      borderColor={borderColor}
      subtleText={subtleText}
      maxH="560px"
      compact={false}
      showEhrNotice={false}
      ehrLoadFailed={ehrLoadFailed}
      onRetry={
        ehrLoadFailed && onReloadTemplates ? onReloadTemplates : undefined
      }
      isRetrying={isReloadingTemplates}
      actionLabel={
        scribeDisabled
          ? 'Clinical Notes Disabled'
          : generatedNote
            ? 'Generate New Note'
            : 'Generate'
      }
      actionLoadingText="Generating..."
      actionAriaLabel={
        generatedNote ? 'Generate a new medical note' : 'Generate medical note'
      }
      actionLeftIcon={
        isGeneratingNote ? (
          <Spinner size="sm" color="white" />
        ) : (
          <TablerBolt size={18} />
        )
      }
      onAction={scribeDisabled ? undefined : clinicalNoteAction}
      isActionDisabled={
        isGeneratingNote ||
        scribeDisabled ||
        (ehrLoadFailed && displayTemplates.length === 0)
      }
      isActionLoading={isGeneratingNote}
      actionBg={RECAP_OPALITE_BLUE}
      actionHoverBg={RECAP_OPALITE_BLUE}
      actionActiveBg={RECAP_OPALITE_BLUE}
      listVariant="clinicalNote"
    />
  );
  const clinicalNoteModal = (
    <Modal
      isOpen={isNoteModalOpen}
      onClose={() => setIsNoteModalOpen(false)}
      isCentered
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        data-testid="clinical-note-generation-modal"
        bg="transparent"
        boxShadow="none"
        maxW={{base: 'calc(100% - 2rem)', md: 'max(50vw, 600px)'}}
        mx={4}
      >
        <IconButton
          aria-label="Close"
          icon={<TablerX size={16} />}
          position="absolute"
          top={RECAP_INFO_CLOSE_BUTTON_TOP}
          right={CREATE_SESSION_CARD_PADDING}
          transform={`translateX(${RECAP_INFO_CLOSE_BUTTON_X_ALIGN_OFFSET})`}
          zIndex={1}
          boxSize={RECAP_INFO_CLOSE_BUTTON_SIZE}
          minW={RECAP_INFO_CLOSE_BUTTON_SIZE}
          p={0}
          bg="transparent"
          color={noteModalStyles.subtleText}
          borderWidth="0"
          borderRadius="0"
          variant="ghost"
          onClick={() => setIsNoteModalOpen(false)}
          _hover={{bg: 'transparent', color: noteModalStyles.textColor}}
          _active={{bg: 'transparent'}}
          _focus={{bg: 'transparent'}}
          _focusVisible={noteModalStyles.focusOutlineStyles}
        />
        <ModalBody p={0}>
          {/* The clinicalNote variant of TemplateSelectionView is a
              self-contained card (title, EHR notice, picker, and the
              "Selected" footer), so the modal only adds elevation. */}
          <Box borderRadius="2xl" boxShadow={noteModalStyles.cardShadow}>
            {clinicalNoteForm}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
  if (generatedNote) {
    const generatedAbs = formatExactTimestamp(generatedNote.generatedAt);
    const editedDate = toDateValue(generatedNote.editedAt);
    const generatedDate = toDateValue(generatedNote.generatedAt);
    // Only surface "Edited" when the edit actually happened later than
    // the original generation (saves can theoretically arrive in the
    // same second and we don't want to chase clock skew).
    const showEdited =
      editedDate &&
      (!generatedDate ||
        editedDate.getTime() - generatedDate.getTime() > 30_000);
    const editedAbs = showEdited ? formatExactTimestamp(editedDate) : null;

    const noteMetaLabel = [
      generatedAbs ? `Generated ${generatedAbs}` : null,
      editedAbs ? `Edited ${editedAbs}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
    const cardPx = {base: 3, sm: 4};
    const topZonePy = dense ? 2.5 : compact ? 3 : 3.5;
    const rowTitleSize = compact ? 'md' : {base: 'md', md: 'lg'};

    return (
      <>
        <VStack
          as="section"
          aria-labelledby="session-section-clinical-note-heading"
          spacing={0}
          align="stretch"
          textAlign="left"
        >
          <RecapSectionHeading
            id="session-section-clinical-note-heading"
            label={onDocumentCall ? 'Documentation' : 'Clinical Note'}
            description={
              onDocumentCall
                ? 'Your AI note for this visit — open it, or document the call again as a new note or a telephone encounter.'
                : 'Your AI note for this visit. Open it to read or copy into the chart, or remake it from a different template.'
            }
            sticky
          />

          <Box
            bg={cardBg}
            {...RECAP_CARD_BOUNDARY_PROPS}
            borderColor={borderColor}
          >
            <Flex
              minW={0}
              px={cardPx}
              py={topZonePy}
              gap={3}
              align="center"
              direction={{base: 'column', sm: 'row'}}
            >
              <VStack align="start" spacing={1} flex={1} minW={0} w="100%">
                <Text
                  fontWeight="normal"
                  fontSize={rowTitleSize}
                  noOfLines={1}
                  lineHeight="1.3"
                  color={noteModalStyles.textColor}
                  minW={0}
                >
                  {generatedNoteTypeLabel}
                </Text>
                {noteMetaLabel ? (
                  <Text
                    color={subtleText}
                    fontSize="sm"
                    fontWeight="normal"
                    lineHeight="1.4"
                  >
                    <Box
                      as="span"
                      display="inline-block"
                      mr={1.5}
                      verticalAlign="-0.125em"
                      lineHeight="1"
                      aria-hidden="true"
                    >
                      <TablerClock size={12} />
                    </Box>
                    {noteMetaLabel}
                  </Text>
                ) : null}
              </VStack>
              <HStack
                spacing={2}
                flexShrink={0}
                w={{base: '100%', sm: 'auto'}}
                alignSelf={{base: 'stretch', sm: 'center'}}
              >
                <Button
                  {...RECAP_GRAY_SECONDARY_BUTTON_PROPS}
                  size="md"
                  flex={{base: 1, sm: 'initial'}}
                  leftIcon={<TablerBolt size={14} aria-hidden="true" />}
                  // With the documentation chooser available, "Generate new"
                  // routes there — the note/telephone choice happens on that
                  // page, so it stays enabled even when notes are disabled.
                  onClick={
                    onDocumentCall
                      ? () => onDocumentCall(session)
                      : () => setIsNoteModalOpen(true)
                  }
                  isDisabled={onDocumentCall ? false : scribeDisabled}
                  aria-label={
                    onDocumentCall
                      ? 'Generate again — new note or telephone encounter'
                      : 'Generate a new clinical note from a template'
                  }
                >
                  Generate new
                </Button>
                <Button
                  {...RECAP_PRIMARY_BUTTON_PROPS}
                  size="md"
                  flex={{base: 1, sm: 'initial'}}
                  leftIcon={<TablerFileText size={14} aria-hidden="true" />}
                  onClick={onViewNote}
                  aria-label="Open and read the clinical note that was already generated"
                >
                  View note
                </Button>
              </HStack>
            </Flex>
          </Box>
        </VStack>

        {clinicalNoteModal}
      </>
    );
  }

  return (
    <>
      <VStack
        as="section"
        aria-labelledby="session-section-clinical-note-heading"
        spacing={0}
        align="stretch"
        textAlign="left"
      >
        <RecapSectionHeading
          id="session-section-clinical-note-heading"
          label={onDocumentCall ? 'Documentation' : 'Clinical Note'}
          description={
            onDocumentCall
              ? 'Generate an AI clinical note for this call, or file it as a telephone encounter.'
              : 'An AI note for this visit. Pick a template and generate one to read or copy into the chart.'
          }
          sticky
        />

        <Box
          bg={cardBg}
          {...RECAP_CARD_BOUNDARY_PROPS}
          borderColor={borderColor}
          borderStyle="dashed"
        >
          <Flex
            direction={{base: 'column', sm: 'row'}}
            align={{base: 'stretch', sm: 'center'}}
            gap={3}
            px={{base: 3, sm: 4}}
            py={dense ? 3 : compact ? 3.5 : 4}
          >
            <VStack align="start" spacing={1} flex={1} minW={0}>
              <Text
                as="h3"
                fontWeight="normal"
                fontSize={PLANNER_SIDEBAR_NAME_FONT_SIZE}
                color={noteModalStyles.textColor}
                lineHeight="1.25"
                noOfLines={1}
              >
                {onDocumentCall ? 'No documentation yet' : 'No note yet'}
              </Text>
            </VStack>
            {/* ONE way in: with the chooser available this button opens the
                Encounter Review page where note-vs-telephone is decided (so
                it isn't gated on scribeDisabled — the telephone path stays
                valid); otherwise it opens the note template modal. */}
            {onDocumentCall ? (
              <Button
                type="button"
                variant="opalitePrimary"
                leftIcon={<TablerFileText size={16} aria-hidden="true" />}
                onClick={() => onDocumentCall(session)}
                px={6}
                w={{base: '100%', sm: 'auto'}}
                aria-label="Generate note — choose a clinical note or a telephone encounter"
              >
                Generate note
              </Button>
            ) : (
              <Button
                type="button"
                variant="opalitePrimary"
                leftIcon={<TablerBolt size={16} aria-hidden="true" />}
                onClick={() => setIsNoteModalOpen(true)}
                isDisabled={scribeDisabled}
                px={6}
                w={{base: '100%', sm: 'auto'}}
              >
                Generate note
              </Button>
            )}
          </Flex>
        </Box>
      </VStack>

      {clinicalNoteModal}
    </>
  );
};

const toDateValue = (value: any) => {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') {
    const converted = value.toDate();
    return Number.isNaN(converted?.getTime?.()) ? null : converted;
  }
  if (typeof value === 'object' && value !== null) {
    const seconds = value.seconds ?? value._seconds;
    const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;
    if (typeof seconds === 'number') {
      const converted = new Date(
        seconds * 1000 + Math.floor(nanoseconds / 1e6),
      );
      return Number.isNaN(converted.getTime()) ? null : converted;
    }
  }
  const converted = new Date(value);
  return Number.isNaN(converted.getTime()) ? null : converted;
};

export const isPlaceholderSessionSummary = (value: any) => {
  const text = String(value ?? '').trim();
  return (
    !text ||
    text === 'Healthcare conversation' ||
    text === 'Healthcare Conversation'
  );
};

export function resolveSessionSummaryDisplay({
  sessionSummary = '',
  encounterSummary = '',
}: Record<string, any> = {}) {
  const cleanEncounterSummary = String(encounterSummary ?? '').trim();
  if (!isPlaceholderSessionSummary(cleanEncounterSummary)) {
    return {
      displaySummary: cleanEncounterSummary,
      summaryHeadingLabel: 'Encounter summary',
      summaryEmptyLabel: 'No encounter summary recorded.',
      useEncounterSummary: true,
    };
  }

  const cleanSessionSummary = String(sessionSummary ?? '').trim();
  return {
    displaySummary: isPlaceholderSessionSummary(cleanSessionSummary)
      ? ''
      : cleanSessionSummary,
    summaryHeadingLabel: 'Session summary',
    summaryEmptyLabel: 'No session summary recorded.',
    useEncounterSummary: false,
  };
}

const RECAP_INFO_CARD_PADDING = {base: 4, md: 5};
const STACKED_INFO_ITEM_KEYS = new Set([
  'bot-id',
  'conversation-id',
  'conversation-ids',
  'transcript-count',
]);

const RecapInfoItem = ({
  action = null,
  helper = null,
  label,
  spanFullWidth = false,
  styles,
  value,
}: Record<string, any>) => (
  <Box
    minW={0}
    py={2}
    gridColumn={spanFullWidth ? {base: '1', md: '1 / -1'} : undefined}
  >
    <Text color={styles.subtleText} fontSize="sm" fontWeight="normal">
      {label}
    </Text>
    <Flex
      align={{base: 'stretch', sm: 'flex-start'}}
      direction={{base: 'column', sm: 'row'}}
      gap={2}
      justify="space-between"
      minW={0}
    >
      <Box flex={1} minW={0}>
        <Text
          color={styles.textColor}
          fontSize="md"
          lineHeight="1.35"
          wordBreak="break-word"
        >
          {value || 'N/A'}
        </Text>
        {helper ? (
          <Text
            color={styles.subtleText}
            fontSize="sm"
            lineHeight="1.45"
            mt={1}
            wordBreak="break-word"
          >
            {helper}
          </Text>
        ) : null}
      </Box>
      {action ? (
        <Box flexShrink={0} alignSelf={{base: 'flex-start', sm: 'center'}}>
          {action}
        </Box>
      ) : null}
    </Flex>
  </Box>
);

export const RecapSectionHeading = ({
  id,
  label,
  description = null,
  badgeLabel = null,
  sticky = false,
}: Record<string, any>) => {
  const headingColor = useColorModeValue('gray.900', 'whiteAlpha.900');
  const descriptionColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const badgeBg = useColorModeValue('gray.200', 'gray.700');
  const badgeColor = useColorModeValue('gray.900', 'gray.100');
  const stickyBg = useColorModeValue('gray.50', 'gray.900');

  return (
    <Flex
      align={description ? 'flex-start' : 'center'}
      justify="space-between"
      gap={3}
      px={1}
      py={sticky ? 1 : undefined}
      mb={2}
      position={sticky ? 'sticky' : undefined}
      top={sticky ? 0 : undefined}
      zIndex={sticky ? 3 : undefined}
      bg={sticky ? stickyBg : undefined}
    >
      <VStack align="start" spacing={0.5} minW={0}>
        <Text
          as="h2"
          id={id}
          fontSize="lg"
          fontWeight="semibold"
          lineHeight="1.3"
          color={headingColor}
          noOfLines={1}
        >
          {label}
        </Text>
        {description ? (
          <Text
            fontSize="sm"
            fontWeight="normal"
            lineHeight="1.35"
            color={descriptionColor}
          >
            {description}
          </Text>
        ) : null}
      </VStack>
      {badgeLabel ? (
        <Badge
          as="span"
          borderRadius="md"
          px={2.5}
          py={0.5}
          minW="2.25rem"
          bg={badgeBg}
          color={badgeColor}
          fontSize="md"
          fontWeight="normal"
          lineHeight="1.4"
          textTransform="none"
          flexShrink={0}
          sx={{fontVariantNumeric: 'tabular-nums'}}
        >
          {badgeLabel}
        </Badge>
      ) : null}
    </Flex>
  );
};

const isTranscriptSegmentMarker = (msg: any) =>
  Boolean(
    msg?.is_segment_marker ||
    msg?.content_type === 'segment_header' ||
    msg?.contentType === 'segment_header',
  );

const formatTranscriptTurnCount = (count: number) =>
  `${count} turn${count === 1 ? '' : 's'}`;

export const MessageBubble = ({
  msg,
  cardBg,
  borderColor,
  subtleText,
  multiContributor = false,
  contributorLabel = null,
}: Record<string, any>) => {
  const contributorAccentColor =
    multiContributor && msg.source_user_id ? 'gray.300' : null;

  // Segment markers arrive when this view is rendering an aggregated
  // multi-conversation encounter — render them as section dividers rather
  // than speaker turns so the chart-prep / visit boundaries are visible
  // without making either look like a participant talked.
  if (isTranscriptSegmentMarker(msg)) {
    const header = (msg.input_text || msg.output_text || '')
      .replace(/^=+\s*|\s*=+$/g, '')
      .trim();
    const dividerColor = contributorAccentColor || borderColor;
    return (
      <HStack my={3} align="center" spacing={3}>
        <Box flex={1} h="2px" bg={dividerColor} borderRadius="lg" />
        <Text
          fontSize="xs"
          fontWeight="normal"
          color={subtleText}
          textTransform="uppercase"
          letterSpacing="wider"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {header}
        </Text>
        <Box flex={1} h="2px" bg={dividerColor} borderRadius="lg" />
      </HStack>
    );
  }

  const isDoctor = msg.is_doctor === true;
  const inputText = msg.input_text || '';
  const outputText = msg.output_text || '';
  const timestamp = toDateValue(msg.timestamp);
  const timeStr = timestamp
    ? timestamp.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

  if (!outputText || outputText === inputText) {
    return (
      <Box
        bg={cardBg}
        {...RECAP_CARD_BOUNDARY_PROPS}
        borderColor={borderColor}
        px={{base: 3, md: 4}}
        py={{base: 2.5, md: 3}}
      >
        <HStack justify="space-between" align="start" spacing={3} mb={1}>
          <Text
            fontSize="xs"
            fontWeight="normal"
            color={subtleText}
            textTransform="uppercase"
            lineHeight="1.4"
            overflowWrap="anywhere"
          >
            {msg.explicitMeaning ||
              msg.explicit_meaning ||
              (isDoctor ? 'Provider' : 'Patient')}
            {contributorLabel ? ` · ${contributorLabel}` : ''}
          </Text>
          {timeStr && (
            <Text fontSize="xs" color={subtleText} flexShrink={0}>
              {timeStr}
            </Text>
          )}
        </HStack>
        <Text
          fontSize="sm"
          lineHeight="1.65"
          overflowWrap="anywhere"
          whiteSpace="pre-wrap"
        >
          {inputText}
        </Text>
      </Box>
    );
  }

  return (
    <VStack align={isDoctor ? 'flex-end' : 'flex-start'} spacing={1} w="100%">
      <Text
        fontSize="sm"
        fontWeight="normal"
        color={isDoctor ? RECAP_OPALITE_BLUE : subtleText}
        textAlign={isDoctor ? 'right' : 'left'}
        px={2}
        maxW={{base: '100%', md: '88%'}}
        lineHeight="1.5"
        overflowWrap="anywhere"
      >
        {inputText}
      </Text>
      <Box
        bg={isDoctor ? RECAP_OPALITE_BLUE : cardBg}
        color={isDoctor ? 'white' : 'inherit'}
        {...(isDoctor ? {borderRadius: 'lg'} : RECAP_CARD_BOUNDARY_PROPS)}
        borderColor={borderColor}
        px={{base: 3, md: 4}}
        py={{base: 2.5, md: 3}}
        maxW={{base: '100%', md: '88%'}}
      >
        <Text
          fontSize={{base: 'sm', md: 'md'}}
          fontWeight="normal"
          lineHeight="1.65"
          overflowWrap="anywhere"
          whiteSpace="pre-wrap"
        >
          {outputText}
        </Text>
      </Box>
      {timeStr && (
        <Text fontSize="xs" color={subtleText} px={2}>
          {timeStr}
        </Text>
      )}
    </VStack>
  );
};

export const TranscriptTab = ({
  transcriptMessages,
  loadingMessages,
  cardBg,
  borderColor,
  subtleText,
  accentColor,
  bodyText,
  mutedPanelBg,
  compact = false,
}: Record<string, any>) => {
  const isMultiContributor = transcriptMessages.some(isTranscriptSegmentMarker);

  return (
    <VStack spacing={compact ? 2 : 3} align="stretch">
      {loadingMessages ? (
        <Flex
          justify="center"
          align="center"
          py={12}
          bg={mutedPanelBg}
          {...RECAP_CARD_BOUNDARY_PROPS}
          borderColor={borderColor}
        >
          <Spinner color={accentColor} size="lg" />
        </Flex>
      ) : transcriptMessages.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          py={12}
          px={4}
          bg={mutedPanelBg}
          {...RECAP_CARD_BOUNDARY_PROPS}
          borderColor={borderColor}
          textAlign="center"
        >
          <TablerMessage size={36} color="#ACACAC" />
          <Text mt={3} fontWeight="normal" color={bodyText}>
            No transcript available
          </Text>
          <Text fontSize="sm" color={subtleText} mt={1}>
            Messages for this session were not found.
          </Text>
        </Flex>
      ) : (
        <VStack spacing={compact ? 2 : 2.5} align="stretch">
          {transcriptMessages.map((msg: any, index: number) => (
            <MessageBubble
              key={msg.id || `transcript-${index}`}
              msg={msg}
              cardBg={cardBg}
              borderColor={borderColor}
              subtleText={subtleText}
              multiContributor={isMultiContributor}
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
};

// ═════════════════════════════════════════════════════════════
// LOADING SKELETON
// ═════════════════════════════════════════════════════════════
//
// Shown in the body while an encounter's messages + note are being
// fetched (the `loadingMessages` window after a recent encounter is
// clicked). Previously the body rendered nothing during this window
// and the Clinical Note + Transcript sections popped in abruptly once
// the fetch resolved. This skeleton keeps the section headings and
// card geometry stable so the layout never shifts — the placeholder
// shimmer simply cross-fades into the real content. The transcript
// bubbles alternate provider (right) / patient (left) to echo the
// real conversation rhythm.
const SKELETON_SHIMMER_SX = {
  '@keyframes session-skeleton-fade-in': {
    from: {opacity: 0, transform: 'translateY(4px)'},
    to: {opacity: 1, transform: 'translateY(0)'},
  },
} as const;

const SkeletonBubble = ({
  align,
  lines,
  width,
  cardBg,
  borderColor,
  delay,
}: Record<string, any>) => {
  const isProvider = align === 'flex-end';
  // Mirror the real MessageBubble palette: provider turns are a solid
  // opalite-blue bubble with white text, so the placeholder lines read as
  // white-on-blue; patient turns are a neutral card with dark text.
  const lineStartColor = isProvider ? 'whiteAlpha.500' : 'blackAlpha.50';
  const lineEndColor = isProvider ? 'whiteAlpha.800' : 'blackAlpha.200';
  return (
    <VStack
      align={align}
      spacing={1.5}
      w="100%"
      sx={SKELETON_SHIMMER_SX}
      animation={`session-skeleton-fade-in 360ms ease-out ${delay}ms both`}
    >
      <Skeleton h="0.7rem" w="4.5rem" borderRadius="full" />
      <Box
        w={width}
        maxW={{base: '100%', md: '88%'}}
        bg={isProvider ? RECAP_OPALITE_BLUE : cardBg}
        borderWidth={isProvider ? 0 : '1px'}
        borderColor={borderColor}
        borderRadius="lg"
        px={{base: 3, md: 4}}
        py={{base: 2.5, md: 3}}
      >
        {Array.from({length: lines}).map((_, idx) => (
          <Skeleton
            key={idx}
            h="0.7rem"
            mb={idx === lines - 1 ? 0 : 1.5}
            w={idx === lines - 1 ? '60%' : '100%'}
            borderRadius="md"
            startColor={lineStartColor}
            endColor={lineEndColor}
          />
        ))}
      </Box>
    </VStack>
  );
};

const SessionDetailLoadingSkeleton = ({
  showNoteTab,
  cardBg,
  borderColor,
  compact = false,
}: Record<string, any>) => {
  const bubbles = [
    {align: 'flex-end', lines: 1, width: '70%'},
    {align: 'flex-start', lines: 2, width: '85%'},
    {align: 'flex-end', lines: 2, width: '80%'},
    {align: 'flex-start', lines: 1, width: '55%'},
  ];

  return (
    <>
      {showNoteTab ? (
        <VStack spacing={0} align="stretch">
          <RecapSectionHeading
            id="session-section-clinical-note-heading"
            label="Clinical Note"
            sticky
          />
          <Box
            bg={cardBg}
            {...RECAP_CARD_BOUNDARY_PROPS}
            borderColor={borderColor}
            px={{base: 3, sm: 4}}
            py={compact ? 3 : 3.5}
            sx={SKELETON_SHIMMER_SX}
            animation="session-skeleton-fade-in 360ms ease-out both"
          >
            <Flex gap={3} align="center" justify="space-between">
              <VStack align="start" spacing={2} flex={1} minW={0}>
                <Skeleton h="1rem" w="60%" borderRadius="md" />
                <Skeleton h="0.7rem" w="40%" borderRadius="md" />
              </VStack>
              {/* Matches the real "Generate new" + "View note" button pair. */}
              <HStack spacing={2} flexShrink={0}>
                <Skeleton h="2.75rem" w="7rem" borderRadius="lg" />
                <Skeleton
                  h="2.75rem"
                  w="5.5rem"
                  borderRadius="lg"
                  startColor="opalite.500"
                  endColor="opalite.600"
                />
              </HStack>
            </Flex>
          </Box>
        </VStack>
      ) : null}

      <Box>
        <RecapSectionHeading
          id="session-section-transcript-heading"
          label="Transcript"
          badgeLabel="Loading"
          sticky
        />
        <VStack spacing={compact ? 3 : 3.5} align="stretch" pt={1}>
          {bubbles.map((bubble, idx) => (
            <SkeletonBubble
              key={idx}
              align={bubble.align}
              lines={bubble.lines}
              width={bubble.width}
              cardBg={cardBg}
              borderColor={borderColor}
              delay={idx * 80}
            />
          ))}
        </VStack>
      </Box>
    </>
  );
};

// ═════════════════════════════════════════════════════════════
// CONTRIBUTORS PANEL
// ═════════════════════════════════════════════════════════════
//
// Collaboration roster surfaced between the session header card and
// the tab bar. Renders only when the parent Encounter has 2+
// contributors — solo sessions skip it entirely so the legacy detail
// view is byte-identical for non-collaborative encounters.
//
// Design choices:
//   - Ordered by first-contribution time so the row reads as a
//     natural visit timeline.
//   - Neutral row markers keep contributor provenance visible without
//     introducing colors outside the recap page's Opalite + grayscale scheme.
//   - "You" annotation on the viewer's own row.
//   - Name resolution: provider_name → userEmail → "Another provider"
//     so the row never shows "Unknown" or an empty label.
//
// We deliberately don't label rows with a prescriptive role (e.g.
// "Chart prep" / "Visit") or surface a "signing" badge — we don't
// actually know which session is which, and we don't yet model who
// signs the note. Time + duration carry the meaning unambiguously.
//
// Accessibility:
//   - Section heading wrapped in <h3> for screen reader navigation.
//   - Min tap targets ≥44pt for any future interactive elements (none
//     today, but the row structure leaves room).
//   - aria-label on each row reads "Dr. Singh, started 9:15 AM, 12
//     minutes" so VoiceOver communicates the whole row without parsing
//     the visual structure.
//   - Color is supplementary; the name + time text carries the meaning.

const ContributorsPanel = ({
  contributors,
  currentUserId,
  cardBg,
  borderColor,
  bodyText,
  subtleText,
}: Record<string, any>) => {
  if (!Array.isArray(contributors) || contributors.length < 2) return null;

  const total = contributors.length;
  const labelFor = (c: any) => {
    if (currentUserId && c.user_id === currentUserId) return 'You';
    const name = (c.provider_name || '').trim();
    if (name && name !== 'You' && name !== 'Another provider') return name;
    const email = (c.userEmail || '').trim();
    if (email) return email;
    return 'Another provider';
  };
  const initialsFor = (label: any) => {
    const parts = (label || '?')
      .replace(/^Dr\.?\s+/i, '')
      .trim()
      .split(/\s+/);
    const first = (parts[0] || '?').charAt(0).toUpperCase();
    const last =
      parts.length > 1 ? parts[parts.length - 1].charAt(0).toUpperCase() : '';
    return (first + last).slice(0, 2);
  };
  const formatTime = (date: any) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'});
  };

  return (
    <Box
      as="section"
      aria-labelledby="contributors-heading"
      bg={cardBg}
      {...RECAP_CARD_BOUNDARY_PROPS}
      borderColor={borderColor}
      px={{base: 4, md: 5}}
      py={{base: 4, md: 5}}
    >
      <HStack mb={3} justify="space-between" align="baseline">
        <Text
          as="h3"
          id="contributors-heading"
          fontSize="xs"
          fontWeight="normal"
          color={subtleText}
          textTransform="uppercase"
          letterSpacing="wider"
        >
          Contributors · {total}
        </Text>
      </HStack>
      <VStack align="stretch" spacing={2.5}>
        {contributors.map((c, idx) => {
          const isViewer = Boolean(
            currentUserId && c.user_id === currentUserId,
          );
          const label = labelFor(c);
          const minutes = Math.max(c.total_minutes || 0, 0);
          const firstAt = formatTime(c.first_at);

          // SR-friendly verbose label. Concatenates the same facts
          // the visual rendering shows so VoiceOver users get the
          // whole picture without parsing the row's micro-layout.
          const ariaLabel = [
            label,
            isViewer ? '(you)' : null,
            firstAt ? `started ${firstAt}` : null,
            minutes ? `${minutes} minute${minutes === 1 ? '' : 's'}` : null,
          ]
            .filter(Boolean)
            .join(', ');

          return (
            <HStack
              key={c.user_id || idx}
              spacing={3}
              align="center"
              aria-label={ariaLabel}
            >
              <Box
                w="1px"
                h="36px"
                borderRadius="lg"
                bg="gray.300"
                flexShrink={0}
                aria-hidden="true"
              />
              <Avatar
                size="sm"
                name={label}
                getInitials={() => initialsFor(label)}
                bg="gray.500"
                color="white"
                aria-hidden="true"
              />
              <Box flex={1} minW={0}>
                <HStack spacing={2} flexWrap="wrap">
                  <Text fontSize="sm" fontWeight="normal" color={bodyText}>
                    {label}
                  </Text>
                  {isViewer && (
                    <Text
                      as="span"
                      fontSize="xs"
                      fontWeight="normal"
                      color={subtleText}
                    >
                      · You
                    </Text>
                  )}
                </HStack>
                <Text fontSize="xs" color={subtleText} mt={0.5}>
                  {firstAt}
                  {firstAt && minutes ? ' · ' : ''}
                  {minutes ? `${minutes} min` : ''}
                </Text>
              </Box>
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════
// REMOVE / RESTORE PILL
// ═════════════════════════════════════════════════════════════
//
// Small destructive (or recovery) action that lives directly under the
// status badge in the detail header. Kept as its own component so the
// hover/disabled/loading states stay consistent regardless of caller.
// A `confirm()` is shown before archiving to protect against stray
// clicks; restore is single-step since it's reversible by another
// click of "Remove".

const RemoveSessionButton = ({
  isRemoved,
  isPending,
  onArchive,
  onRestore,
}: Record<string, any>) => {
  const isRestoreMode = Boolean(isRemoved);
  const handler = isRestoreMode ? onRestore : onArchive;

  if (!handler) return null;

  const handleClick = () => {
    if (isPending) return;
    if (isRestoreMode) {
      handler();
      return;
    }
    const ok =
      typeof window !== 'undefined'
        ? window.confirm(
            'Remove this session from your main list? You can restore it anytime from the Removed sessions section.',
          )
        : true;
    if (ok) handler();
  };

  const styles = isRestoreMode
    ? {
        label: 'Restore',
        ariaLabel: 'Restore session',
        icon: <TablerRotate size={14} />,
      }
    : {
        label: 'Remove',
        ariaLabel: 'Remove session',
        icon: <TablerTrash size={14} />,
      };

  return (
    <Button
      {...RECAP_GRAY_SECONDARY_BUTTON_PROPS}
      size="md"
      onClick={handleClick}
      isDisabled={isPending}
      isLoading={isPending}
      loadingText={isRestoreMode ? 'Restoring…' : 'Removing…'}
      leftIcon={styles.icon}
      fontSize="sm"
      px={4}
      aria-label={styles.ariaLabel}
    >
      {styles.label}
    </Button>
  );
};

// ═════════════════════════════════════════════════════════════
// SESSION DETAIL VIEW (focused, workflow-first layout)
// ═════════════════════════════════════════════════════════════

const SessionDetailView = ({
  session,
  messages,
  loadingMessages,
  selectedTemplateId,
  setSelectedTemplateId,
  generatedNote,
  isGeneratingNote,
  onGenerateNote,
  onViewNote,
  onRegenerate,
  onBack,
  onEditPatient = null,
  // When true (and the session has a conversation id), the overflow menu
  // exposes a low-key "Rating" item that opens a compact rate/feedback modal.
  // Off by default so admin/read-only surfaces don't invite providers to rate.
  enableSessionRating = false,
  showInfoButton = true,
  showRemoveButton = true,
  bgColor,
  cardBg,
  borderColor,
  subtleText,
  allTemplates,
  backLabel,
  backButtonTestId,
  showNoteTab = true,
  // Optional node rendered in the header's right-hand action column,
  // before the status/continue/info/remove controls. Lets a host surface
  // inject its own status pill + primary action (e.g. the company phone
  // call view's "Company-shared" badge + "Create telephone encounter")
  // without forking the whole detail layout.
  headerExtra = null,
  // Optional node rendered in the body where the clinical-note panel sits
  // (directly under the header, above the transcript). Lets a host surface
  // (e.g. the phone call view's "Create tel encounter" card) occupy the same
  // slot the regular interpreter view uses for its note.
  bodyLeadingSlot = null,
  // Optional. When provided AND the encounter is an interpreter/telephonic
  // call, the note section becomes a "Documentation" card whose single action
  // opens the unified Encounter Review chooser (clinical note vs telephone
  // encounter) — one button here, the fork on the next screen. Called with
  // the session.
  onDocumentCall = null,
  extraOverviewItems = [],
  scribeDisabled = false,
  ehrLoadFailed = false,
  onReloadTemplates = null,
  isReloadingTemplates = false,
  // Optional soft-delete controls. When `onArchive` or `onRestore` is
  // provided the header renders an inline Remove / Restore pill under
  // the status badge so the destructive action is discoverable from
  // anywhere this view is used. `isRemoved` flips which action shows
  // and which copy/colors are applied. `isSessionPending` lets callers
  // disable the button mid-write to prevent double-clicks.
  onArchive = null,
  onRestore = null,
  isRemoved = false,
  isSessionPending = false,
  // Optional encounter controls. When `onContinueEncounter` is provided
  // and the session is closed, the header action menu exposes a
  // "Continue encounter" option that hands off to the parent's
  // `useContinueEncounter` flow. When
  // `onMarkDoneEncounter` is provided and the session is still
  // recording, the "In progress" badge becomes a menu button — clicking
  // it reveals a "Mark as completed" action. Both controls degrade
  // gracefully when the props are omitted (admin surfaces, removed-
  // sessions disclosure, etc.) so the badge stays static there.
  onContinueEncounter = null,
  isContinuing = false,
  onMarkDoneEncounter = null,
  isMarkingDone = false,
  // Viewing provider's user_id. When supplied and the session was
  // created by a different provider, the header surfaces a "Started by
  // Dr. X" attribution line so doctors know whose recording they're about
  // to add to (or whose note they're reviewing). When omitted (admin pages,
  // bot session pages, etc.) the cross-provider treatment is suppressed and
  // the header looks exactly as it did before.
  currentUserId = null,
  // Collaboration roster for the parent Encounter. When this session
  // is part of a multi-contributor encounter, passing in the
  // contributors array makes the header render a "Contributors"
  // panel — one row per provider, ordered chronologically, with
  // avatar + name + start time + minutes. Omitted / empty arrays and
  // single-contributor encounters skip the panel entirely so legacy
  // non-collaborative sessions stay visually unchanged.
  encounterContributors = [],
  // Summary written to the Encounter doc. When non-empty, the recap card
  // shows it as "Encounter summary"; otherwise it falls back to this
  // Conversation's `session.summary` as "Session summary".
  encounterSummary = '',
  // Minimum height of the outer background box. Defaults to "100vh" so
  // standalone full-page surfaces (ScribePage, bot detail,
  // admin) paint the gray backdrop edge-to-edge. The Hub embeds this view
  // inside its own fixed-height, scrollable detail pane and passes "100%"
  // — that stops the view from forcing an extra ~viewport-tall block
  // (which guaranteed a scrollbar even when the content easily fit).
  containerMinH = '100vh',
  containerMaxW = '4xl',
  containerPx = {base: 4, md: 6},
  containerPt = null,
}: Record<string, any>) => {
  const accentColor = RECAP_OPALITE_BLUE;
  const startTime = toDateValue(session.start_time || session.startedAt);
  const endTime = toDateValue(session.end_time || session.updatedAt);
  const canContinueEncounter = Boolean(
    endTime && !isRemoved && onContinueEncounter,
  );
  const ratingConversationId = session.id || session.conversation_id || null;
  const canRate = Boolean(enableSessionRating && ratingConversationId);
  const ratingDisclosure = useDisclosure();
  // Local mirror of the saved rating so the menu label + modal reflect a
  // just-submitted rating without waiting on a parent refetch.
  const [localRating, setLocalRating] = useState<number | null>(
    session.rating ?? null,
  );
  const [localReason, setLocalReason] = useState<string>(
    session.rating_reason || '',
  );
  const hasEncounterActionMenu = Boolean(onEditPatient || canRate);
  const minutes = session.minutes || 0;
  const summary = session.summary || '';
  const transcriptMessages = useMemo(
    () =>
      messages
        .filter(
          (msg: any) =>
            (msg.content_type || msg.contentType) !== 'dictated_note',
        )
        .filter((msg: any, idx: any, arr: any) => {
          if (idx === 0) return true;
          const prev = arr[idx - 1];
          return (
            (msg.input_text || '') !== (prev.input_text || '') ||
            (msg.output_text || '') !== (prev.output_text || '')
          );
        }),
    [messages],
  );

  const mutedPanelBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const bodyText = useColorModeValue('gray.900', 'whiteAlpha.900');
  const infoModalStyles = usePlannerStyles();

  // ── Container-width awareness (NOT viewport width) ──────────────
  // This view is embedded in surfaces of wildly different widths: a
  // full-page route (≈ whole viewport) AND the Hub's master/detail
  // pane (≈ half the viewport). Chakra's responsive props key off the
  // *viewport*, so in the narrow Hub pane the header still thought it
  // was a roomy "desktop" and laid itself out as a row — the fixed
  // action buttons then crushed the title column until the session
  // title wrapped one character per line. We measure our own rendered
  // width with a ResizeObserver and drive the header layout / title
  // size / note-card arrangement off *that*, so the view reflows
  // correctly no matter how wide the container actually is.
  //
  // The callback ref measures synchronously on mount (before paint) and
  // keeps a ResizeObserver attached for live resizes, so there is no
  // broken first frame and dragging the window reflows smoothly.
  const [containerWidth, setContainerWidth] = useState(0);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const resizeObserverRef = useRef<any>(null);
  const measureRef = useCallback((node: any) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (!node) return;
    setContainerWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(entries => {
        const w = entries[0]?.contentRect?.width;
        if (w) setContainerWidth(w);
      });
      ro.observe(node);
      resizeObserverRef.current = ro;
    }
  }, []);
  // Width gates (measured incl. the container's own horizontal padding,
  // so the inner content is ~48px narrower than these numbers).
  //   isNarrow      → header stacks (title above the action buttons)
  //   isVeryNarrow  → tighten title + action sizing further
  // `containerWidth === 0` means "not measured yet"; we treat that as
  // not-narrow because the synchronous mount measurement fills it in
  // before the first paint in practice.
  const isNarrow = containerWidth > 0 && containerWidth < 640;
  const isVeryNarrow = containerWidth > 0 && containerWidth < 460;

  // Height-aware density. The CIFC workstations (and other short
  // displays) couldn't fit the header → note cards stack in the
  // viewport, forcing a scroll just to reach "Generate a new note". We
  // key off the *viewport height* (not width) and progressively tighten
  // vertical rhythm so the whole detail view stays above the fold.
  //   compact  ≈ laptop / 768-tall monitors with browser + app chrome
  //   dense    ≈ very short panes (≤ ~640px of usable height)
  // `ssr: false` + a falsy fallback keeps the first paint at the roomy
  // desktop spacing and only tightens once the match resolves, so tall
  // displays are visually unchanged.
  const [isShortViewport, isTinyViewport] = useMediaQuery(
    ['(max-height: 860px)', '(max-height: 700px)'],
    {ssr: false, fallback: [false, false]},
  );
  const compact = isShortViewport;
  const dense = isTinyViewport;

  // Derived vertical-rhythm tokens. Pulled out so the header card, the
  // outer padding, the section spacing and the section header all shrink in
  // lockstep on short displays.
  const outerPt = dense ? 3 : compact ? 4 : 6;
  const outerPb = dense ? 3 : compact ? 4 : 8;
  const sectionSpacing = dense ? 2 : compact ? 3 : 4;
  const headerPy = compact ? {base: 3, md: 3} : {base: 4, md: 5};
  const headerStackSpacing = compact ? 1.5 : 2;

  // Header layout driven by measured width, not the viewport. When the
  // container is narrow we stack the title above the action buttons so
  // neither crowds the other; when it's roomy we keep the title/actions
  // side-by-side as before.
  const headerDirection = isNarrow ? 'column' : 'row';
  const headerAlign = isNarrow ? 'stretch' : 'flex-start';
  const headerGap = isNarrow ? 3 : 4;
  const summaryFontSize = isVeryNarrow ? 'lg' : '2xl';

  const formatTime = (d: any) =>
    d
      ? d.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})
      : 'Unavailable';

  const formatSessionDate = (d: any) => {
    if (!d) return 'Unavailable';
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Cross-provider attribution. Resolved name → email → placeholder so
  // the label always reads naturally even when older accounts didn't
  // have a `provider_name` set. Suppressed entirely when the viewer
  // recorded this session (the common case) or when the caller didn't
  // pass `currentUserId` (admin / bot pages).
  const sessionUserId = session.user_id || session.userId || null;
  const isShared = Boolean(
    currentUserId && sessionUserId && sessionUserId !== currentUserId,
  );
  const sharedByLabel = (() => {
    const name = (session.provider_name || session.providerName || '').trim();
    if (name) return name;
    const email = (session.userEmail || session.user_email || '').trim();
    if (email) return email;
    return 'another provider';
  })();
  const dateLabel = formatSessionDate(startTime);
  const timeLabel = startTime
    ? `${formatTime(startTime)}${endTime ? ` - ${formatTime(endTime)}` : ''}`
    : 'Unavailable';
  const durationLabel = `${Math.max(minutes, 1)} min`;
  const rawLanguage =
    session.foreignLanguage ||
    session.foreign_language ||
    session.language ||
    session.__encounterContext?.foreign_language ||
    session.__encounterContext?.foreignLanguage ||
    session.__encounterContext?.language ||
    (session.__encounterContext?.conversations || [])
      .map(
        (conversation: any) =>
          conversation?.foreignLanguage ||
          conversation?.foreign_language ||
          conversation?.language,
      )
      .find(Boolean) ||
    '';
  const languageLabel = isNonEnglish(rawLanguage)
    ? `${languageFlag(rawLanguage)} ${displayLanguageName(rawLanguage)}`
    : '';
  const isInterpreterEncounter = !resolveEncounterModality(session).isScribe;

  const {displaySummary, summaryEmptyLabel} = resolveSessionSummaryDisplay({
    sessionSummary: summary,
    encounterSummary,
  });
  const recapTitle = displaySummary || summaryEmptyLabel;
  // Build the header metadata lines. We collapse unavailable values to
  // nothing rather than showing the literal string, because a row of
  // "Unavailable · Unavailable" reads as broken.
  const metaLineParts = [
    startTime ? dateLabel : null,
    startTime ? timeLabel : null,
  ].filter(Boolean);
  const detailMetaLineItems = [
    minutes ? {key: 'duration', label: durationLabel} : null,
    isInterpreterEncounter ? {key: 'modality', label: 'Interpreter'} : null,
    languageLabel ? {key: 'language', label: languageLabel} : null,
    isShared
      ? {
          key: 'started-by',
          label: `Started by ${sharedByLabel}`,
          icon: TablerUsers,
          color: bodyText,
        }
      : null,
  ].filter(Boolean);
  const transcriptTurnCount = useMemo(
    () =>
      transcriptMessages.filter((msg: any) => !isTranscriptSegmentMarker(msg))
        .length,
    [transcriptMessages],
  );
  const transcriptBadgeLabel = loadingMessages
    ? 'Loading'
    : formatTranscriptTurnCount(transcriptTurnCount);

  // The header now carries patient / date / time / duration / status, so
  // the Info modal only needs to surface extras a caller chose to add (e.g.
  // Bot Conversation ID on the admin page). This keeps us from showing the
  // same rows in two places.
  const overviewItems = extraOverviewItems;
  const hasInfoContent = overviewItems.length > 0;
  const shouldRenderLoadedSections = !loadingMessages;

  // "Generate note" chooser for interpreter/telephonic calls only — scribe
  // visits keep the plain clinical-note card.
  const documentCallHandler =
    onDocumentCall && isInterpreterEncounter ? onDocumentCall : null;

  const clinicalNotePanel =
    showNoteTab && shouldRenderLoadedSections ? (
      <NoteGenerationTab
        allTemplates={allTemplates}
        selectedTemplateId={selectedTemplateId}
        setSelectedTemplateId={setSelectedTemplateId}
        generatedNote={generatedNote}
        isGeneratingNote={isGeneratingNote}
        onGenerateNote={onGenerateNote}
        onViewNote={onViewNote}
        onRegenerate={onRegenerate}
        session={session}
        messages={messages}
        accentColor={accentColor}
        cardBg={cardBg}
        borderColor={borderColor}
        subtleText={subtleText}
        scribeDisabled={scribeDisabled}
        ehrLoadFailed={ehrLoadFailed}
        onReloadTemplates={onReloadTemplates}
        isReloadingTemplates={isReloadingTemplates}
        onDocumentCall={documentCallHandler}
        compact={compact}
        dense={dense}
      />
    ) : null;

  return (
    <Box bg={bgColor} minH={containerMinH} pb={outerPb}>
      <Container
        ref={measureRef}
        maxW={containerMaxW}
        pt={containerPt ?? outerPt}
        px={containerPx}
      >
        {onBack ? (
          <DetailBackButton
            label={backLabel || 'Back'}
            onClick={onBack}
            testId={backButtonTestId}
            mb={dense ? 2 : 3}
          />
        ) : null}
        <VStack spacing={sectionSpacing} align="stretch">
          <Box
            bg={infoModalStyles.surfaceBg}
            borderWidth="1px"
            borderStyle={isRemoved ? 'dashed' : 'solid'}
            borderColor={infoModalStyles.borderColor}
            borderRadius="xl"
            overflow="hidden"
            px={{base: 4, md: 5}}
            py={headerPy}
            position="relative"
            opacity={isRemoved ? 0.95 : 1}
          >
            {isRemoved && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                px={{base: 4, md: 5}}
                py={2}
                bg="gray.50"
                borderTopRadius="xl"
                borderBottomWidth="1px"
                borderColor={infoModalStyles.borderColor}
                display="flex"
                alignItems="center"
                gap={2}
                color="gray.700"
                fontSize="xs"
                fontWeight="normal"
                textTransform="uppercase"
                letterSpacing="0.04em"
              >
                <TablerTrash size={12} />
                <Text>Removed — hidden from your main list</Text>
              </Box>
            )}
            <Flex
              justify="space-between"
              align={headerAlign}
              direction={headerDirection}
              gap={headerGap}
              mt={isRemoved ? 7 : 0}
            >
              {/* Left column: date/time → title → detail meta → attribution.
                    `flex={1}` + `minW={0}` lets long patient names or
                    session titles wrap inside this column on narrow
                    screens instead of pushing the status pill off the
                    edge or forcing the parent Flex into overflow. */}
              <VStack
                align="start"
                spacing={headerStackSpacing}
                flex={1}
                minW={0}
                w="100%"
              >
                {metaLineParts.length > 0 && (
                  <Text
                    fontSize="md"
                    color={bodyText}
                    lineHeight="1.2"
                    noOfLines={1}
                    fontWeight="normal"
                    sx={{fontVariantNumeric: 'tabular-nums'}}
                  >
                    {metaLineParts.join(' · ')}
                  </Text>
                )}
                <Text
                  fontWeight="normal"
                  fontSize={summaryFontSize}
                  letterSpacing="0"
                  color={bodyText}
                  lineHeight="1.4"
                  // `overflowWrap` (not the legacy `wordBreak:
                  // break-word`) so a constrained column wraps between
                  // words and only breaks inside a word as a last
                  // resort — the title can never shatter to one
                  // character per line again.
                  overflowWrap="anywhere"
                >
                  {recapTitle}
                </Text>
                {detailMetaLineItems.length > 0 && (
                  <Flex
                    as="p"
                    fontSize="sm"
                    fontWeight="normal"
                    lineHeight="1.35"
                    color={subtleText}
                    align="center"
                    flexWrap="wrap"
                    columnGap={1.5}
                    rowGap={1}
                  >
                    {detailMetaLineItems.map((item: any, index: any) => (
                      <Box
                        as="span"
                        key={item.key}
                        display="inline-flex"
                        alignItems="center"
                        gap={1.5}
                        minW={0}
                      >
                        {index > 0 ? (
                          <Box as="span" aria-hidden="true">
                            •
                          </Box>
                        ) : null}
                        <Box
                          as="span"
                          display="inline-flex"
                          alignItems="center"
                          gap={1}
                          color={item.color || subtleText}
                          minW={0}
                        >
                          {item.icon ? (
                            <Box
                              as={item.icon}
                              boxSize="13px"
                              aria-hidden="true"
                              flexShrink={0}
                            />
                          ) : null}
                          <Box as="span" minW={0}>
                            {item.label}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Flex>
                )}
              </VStack>

              {/* Right column: status pill + Remove. On mobile this
                    stacks BELOW the left column (parent direction is
                    column at `base`) and aligns to the start so it
                    reads as a continuation rather than a corner
                    affordance. On desktop it tucks to the top-right. */}
              <HStack
                spacing={2}
                align="center"
                alignSelf="flex-start"
                flexShrink={0}
                flexWrap="wrap"
                w={isNarrow ? '100%' : 'auto'}
                mt={isNarrow ? 1 : 0}
              >
                {headerExtra}

                {/* Status pill. When the session is still recording
                      AND the caller wired up an `onMarkDoneEncounter`
                      handler, the badge becomes an interactive Menu
                      button — clicking it reveals "Mark as completed."
                      That makes "In progress" the natural affordance
                      for closing out an orphan (browser crashed mid-
                      recording, etc.) instead of leaving providers to
                      hunt around for a hidden control. When the badge
                      isn't interactive (note already completed, or
                      no Mark Done wiring in this surface) we render
                      the original static Badge so screen readers don't
                      announce a fake menu. */}
                {!endTime && onMarkDoneEncounter ? (
                  <Menu placement="bottom-end" closeOnSelect>
                    <MenuButton
                      {...RECAP_GRAY_SECONDARY_BUTTON_PROPS}
                      as={Button}
                      size="md"
                      px={3}
                      fontSize="sm"
                      rightIcon={
                        <TablerChevronDown size={14} aria-hidden="true" />
                      }
                      isLoading={isMarkingDone}
                      loadingText="Closing"
                      aria-label="This session is in progress. Open menu to mark it as completed."
                    >
                      In progress
                    </MenuButton>
                    <Portal>
                      <MenuList
                        minW="220px"
                        borderColor={borderColor}
                        borderRadius="lg"
                        shadow="lg"
                        zIndex="popover"
                      >
                        <MenuItem
                          icon={<TablerCircleCheck aria-hidden="true" />}
                          onClick={onMarkDoneEncounter}
                          isDisabled={isMarkingDone}
                        >
                          Mark as completed
                        </MenuItem>
                      </MenuList>
                    </Portal>
                  </Menu>
                ) : null}

                {/* Overflow menu — sits to the LEFT of Continue Encounter so
                      the kebab leads into the primary resume action rather
                      than trailing the whole control cluster. */}
                {hasEncounterActionMenu ? (
                  <Menu placement="bottom-end" closeOnSelect>
                    <MenuButton
                      as={IconButton}
                      type="button"
                      size="md"
                      icon={<TablerDotsVertical size={18} aria-hidden="true" />}
                      variant="ghost"
                      color={subtleText}
                      borderRadius="lg"
                      minW="44px"
                      h="44px"
                      aria-label="Encounter options"
                      _hover={{bg: mutedPanelBg, color: bodyText}}
                      _active={{bg: mutedPanelBg}}
                      _focusVisible={{boxShadow: 'outline'}}
                    />
                    <Portal>
                      <MenuList
                        minW="210px"
                        borderColor={borderColor}
                        borderRadius="lg"
                        shadow="lg"
                        zIndex="popover"
                      >
                        {onEditPatient ? (
                          <MenuItem
                            icon={<TablerEdit size={16} aria-hidden="true" />}
                            onClick={e => {
                              e.stopPropagation();
                              onEditPatient();
                            }}
                          >
                            Edit Details
                          </MenuItem>
                        ) : null}
                        {canRate ? (
                          <MenuItem
                            icon={<TablerStar size={16} aria-hidden="true" />}
                            onClick={e => {
                              e.stopPropagation();
                              ratingDisclosure.onOpen();
                            }}
                          >
                            {localRating ? 'Rating' : 'Rate session'}
                          </MenuItem>
                        ) : null}
                      </MenuList>
                    </Portal>
                  </Menu>
                ) : null}

                {/* Continue Encounter — first-class header action so
                      providers can resume a closed encounter (add a new
                      recording session that merges into the same note) in one
                      click. Uses the secondary outline style to match the
                      "Link Patient" affordance. Renders whenever the encounter
                      is closed and the caller wired up `onContinueEncounter`. */}
                {canContinueEncounter ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="md"
                    whiteSpace="nowrap"
                    leftIcon={<TablerPlayerPlay size={16} aria-hidden="true" />}
                    isLoading={isContinuing}
                    loadingText="Opening"
                    onClick={e => {
                      e.stopPropagation();
                      onContinueEncounter?.();
                    }}
                    aria-label={`Continue this encounter${session.patient_name ? ` for ${session.patient_name}` : ''} - add a new recording session that will be merged into the same note.`}
                  >
                    Continue Encounter
                  </Button>
                ) : null}

                {showInfoButton ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="md"
                    whiteSpace="nowrap"
                    leftIcon={<TablerInfoCircle size={16} aria-hidden="true" />}
                    onClick={() => setIsInfoOpen(true)}
                    aria-label="Open encounter info"
                  >
                    Info
                  </Button>
                ) : null}

                {showRemoveButton && (onArchive || onRestore) && (
                  <RemoveSessionButton
                    isRemoved={isRemoved}
                    isPending={isSessionPending}
                    onArchive={onArchive}
                    onRestore={onRestore}
                  />
                )}
              </HStack>
            </Flex>
          </Box>

          {bodyLeadingSlot}

          {shouldRenderLoadedSections ? (
            <VStack
              spacing={sectionSpacing}
              align="stretch"
              sx={SKELETON_SHIMMER_SX}
              animation="session-skeleton-fade-in 320ms ease-out both"
            >
              {clinicalNotePanel}

              {/* Contributors panel — surfaces the full collaboration
                    roster for the parent Encounter. Renders only when there
                    are 2+ contributors so legacy / solo sessions look
                    exactly as they did before. Ordered by first-contribution
                    time so the row reads as a natural timeline of the visit. */}
              <ContributorsPanel
                contributors={encounterContributors}
                currentUserId={currentUserId}
                cardBg={cardBg}
                borderColor={borderColor}
                bodyText={bodyText}
                subtleText={subtleText}
              />

              <RecapSectionHeading
                id="session-section-transcript-heading"
                label="Transcript"
                badgeLabel={transcriptBadgeLabel}
                sticky
              />

              <Box
                as="section"
                aria-labelledby="session-section-transcript-heading"
              >
                <TranscriptTab
                  transcriptMessages={transcriptMessages}
                  loadingMessages={loadingMessages}
                  cardBg={cardBg}
                  borderColor={borderColor}
                  subtleText={subtleText}
                  accentColor={accentColor}
                  bodyText={bodyText}
                  mutedPanelBg={mutedPanelBg}
                  compact={compact}
                />
              </Box>
            </VStack>
          ) : (
            <SessionDetailLoadingSkeleton
              showNoteTab={showNoteTab}
              cardBg={cardBg}
              borderColor={borderColor}
              compact={compact}
            />
          )}
        </VStack>

        {showInfoButton ? (
          <Modal
            isOpen={isInfoOpen}
            onClose={() => setIsInfoOpen(false)}
            isCentered
            size="xl"
            scrollBehavior="inside"
          >
            <ModalOverlay />
            <ModalContent
              bg="transparent"
              boxShadow="none"
              w="calc(100% - 2rem)"
              maxW="750px"
              mx={4}
            >
              <IconButton
                aria-label="Close"
                icon={<TablerX size={16} />}
                position="absolute"
                top={RECAP_INFO_CLOSE_BUTTON_TOP}
                right={RECAP_INFO_CARD_PADDING}
                transform={`translateX(${RECAP_INFO_CLOSE_BUTTON_X_ALIGN_OFFSET})`}
                zIndex={1}
                boxSize={RECAP_INFO_CLOSE_BUTTON_SIZE}
                minW={RECAP_INFO_CLOSE_BUTTON_SIZE}
                p={0}
                bg="transparent"
                color={infoModalStyles.subtleText}
                borderWidth="0"
                borderRadius="0"
                variant="ghost"
                onClick={() => setIsInfoOpen(false)}
                _hover={{bg: 'transparent', color: infoModalStyles.textColor}}
                _active={{bg: 'transparent'}}
                _focus={{bg: 'transparent'}}
                _focusVisible={infoModalStyles.focusOutlineStyles}
              />
              <ModalBody p={0}>
                <Box
                  bg={infoModalStyles.surfaceBg}
                  borderWidth="1px"
                  borderColor={infoModalStyles.borderColor}
                  borderRadius="xl"
                  boxShadow={infoModalStyles.cardShadow}
                  overflow="hidden"
                >
                  <VStack
                    spacing={4}
                    align="stretch"
                    p={RECAP_INFO_CARD_PADDING}
                  >
                    <Text
                      as="h2"
                      color={infoModalStyles.textColor}
                      fontSize="2xl"
                      fontWeight="semibold"
                      lineHeight="1.25"
                      letterSpacing="0"
                      mb={0}
                      pt={1}
                      pb={2}
                      pr={10}
                      noOfLines={2}
                      wordBreak="break-word"
                    >
                      Info
                    </Text>

                    <Box
                      as="section"
                      aria-labelledby="encounter-info-details-heading"
                    >
                      <Flex
                        gap={{base: 2, md: 3}}
                        align={{base: 'stretch', md: 'flex-start'}}
                        direction={{base: 'column', md: 'row'}}
                      >
                        <Text
                          as="h3"
                          id="encounter-info-details-heading"
                          color={infoModalStyles.textColor}
                          fontSize="sm"
                          fontWeight="semibold"
                          lineHeight="1.25"
                          minH="auto"
                          minW={{md: '8rem'}}
                          pt={2}
                        >
                          Details
                        </Text>
                        <Box flex="1" minW={0}>
                          {hasInfoContent ? (
                            <Grid
                              templateColumns={{
                                base: '1fr',
                                md: 'repeat(2, minmax(0, 1fr))',
                              }}
                              gap={3}
                              w="100%"
                            >
                              {overviewItems.map((item: any) => (
                                <RecapInfoItem
                                  key={item.key}
                                  label={item.label}
                                  value={item.value}
                                  helper={item.helper}
                                  action={item.action}
                                  styles={infoModalStyles}
                                  spanFullWidth={STACKED_INFO_ITEM_KEYS.has(
                                    item.key,
                                  )}
                                />
                              ))}
                            </Grid>
                          ) : (
                            <Text
                              fontSize="sm"
                              color={infoModalStyles.subtleText}
                              py={2}
                            >
                              No additional details available.
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    </Box>
                  </VStack>
                </Box>
              </ModalBody>
            </ModalContent>
          </Modal>
        ) : null}

        {canRate ? (
          <SessionRatingModal
            isOpen={ratingDisclosure.isOpen}
            onClose={ratingDisclosure.onClose}
            conversationId={ratingConversationId}
            initialRating={localRating}
            initialReason={localReason}
            accentColor={accentColor}
            onRated={(newRating, newReason) => {
              setLocalRating(newRating);
              setLocalReason(newReason);
            }}
          />
        ) : null}
      </Container>
    </Box>
  );
};

export default SessionDetailView;
